import { useEffect, useMemo } from "react";
import { useSearchParams } from "react-router-dom";
import { Mail, Phone, MapPin, Clock } from "lucide-react";
import SectionHead from "@/components/SectionHead";
import { Reveal } from "@/components/Reveal";
import EnquiryForm from "@/components/EnquiryForm";
import DemoBooking from "@/components/DemoBooking";
import { waLink } from "@/lib/api";
import { useSettings } from "@/lib/useSettings";
import { WhatsAppIcon } from "@/components/FloatingWhatsApp";

export default function Contact() {
  const settings = useSettings();
  const [params] = useSearchParams();
  const preService = params.get("service");

  const defaultServices = useMemo(() => {
    if (!preService) return [];
    const match = [
      "Website Development", "AI Voice Agent", "WhatsApp AI Agent", "Website Chatbot",
      "Instagram AI Agent", "Facebook AI Agent", "CRM Integration", "Complete Automation Solution",
    ].find((s) => preService.toLowerCase().includes(s.toLowerCase().split(" ")[0]) || preService.includes(s));
    return match ? [match] : [preService];
  }, [preService]);

  useEffect(() => {
    document.title = "Contact Us — Get a Free Consultation | RIDGELINE DIGI PARTNER";
  }, []);

  const infoItems = [
    { icon: Phone, label: "Phone", value: settings?.business_phone },
    { icon: Mail, label: "Business Email", value: settings?.business_email },
    { icon: MapPin, label: "Address", value: settings?.business_address },
    { icon: Clock, label: "Business Hours", value: settings?.business_hours },
  ].filter((i) => i.value);

  return (
    <div className="pt-[84px]">
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 hero-glow" />
        <div className="relative max-w-7xl mx-auto px-5 sm:px-8 py-20 sm:py-24">
          <SectionHead eyebrow="CONTACT US" title="Let's Build Your Next Digital Advantage."
            description="Tell us about your business goals. Our team will help you explore the right website, AI agent, and automation solutions for your requirements." />
          <div className="grid lg:grid-cols-5 gap-10 items-start">
            <div className="lg:col-span-3">
              <EnquiryForm sourcePage={preService ? `contact:${preService}` : "contact"} defaultServices={defaultServices} />
            </div>
            <div className="lg:col-span-2 space-y-5">
              <Reveal delay={0.1}>
                <a href={waLink(settings)} target="_blank" rel="noopener noreferrer"
                  data-testid="contact-whatsapp-card"
                  className="block glass-card rounded-2xl p-7 transition-all duration-300 hover:border-green-500/50 hover:-translate-y-1">
                  <span className="flex h-11 w-11 items-center justify-center rounded-xl bg-green-500/15 border border-green-500/30 text-green-400">
                    <WhatsAppIcon size={20} />
                  </span>
                  <h3 className="mt-4 font-display text-lg font-semibold text-slate-50">Chat on WhatsApp</h3>
                  <p className="mt-2 text-sm text-slate-400 leading-relaxed">
                    Prefer a quick conversation? Message us directly and we will respond as soon as possible.
                  </p>
                  <span className="mt-4 inline-block text-xs font-semibold text-green-400">Start a chat &rarr;</span>
                </a>
              </Reveal>
              {infoItems.length > 0 && (
                <Reveal delay={0.2}>
                  <div className="glass-card rounded-2xl p-7 space-y-4" data-testid="contact-business-info">
                    {infoItems.map((item) => (
                      <div key={item.label} className="flex items-start gap-3">
                        <item.icon size={16} className="mt-1 shrink-0 text-blue-400" />
                        <div>
                          <p className="text-xs font-mono2 tracking-[0.2em] text-slate-500">{item.label.toUpperCase()}</p>
                          <p className="mt-1 text-sm text-slate-200 leading-relaxed">{item.value}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </Reveal>
              )}
            </div>
          </div>
        </div>
      </section>

      <section id="demo-booking" className="scroll-mt-24 border-t border-white/5 bg-slate-950/50">
        <div className="max-w-3xl mx-auto px-5 sm:px-8 py-20">
          <Reveal className="text-center mb-10">
            <p className="font-mono2 text-xs tracking-[0.25em] text-blue-400 mb-3">BOOK A LIVE DEMO</p>
            <h2 className="font-display text-2xl sm:text-3xl font-bold text-slate-50">See It Working Before You Decide.</h2>
            <p className="mt-3 text-sm text-slate-400">
              Pick any day that suits you — live demo slots are available between 2:00 PM and 4:00 PM IST.
            </p>
          </Reveal>
          <DemoBooking />
        </div>
      </section>
    </div>
  );
}
